import { useState } from "react";
import { ApiError, apiPost } from "../api/client";
import { navigate } from "../lib/router";
import { useAppData } from "../state/AppData";
import { PacketBytesView } from "../components/PacketBytesSection";
import { PacketsViewSwitch, packetsViewHash } from "../components/PacketsViewSwitch";
import { EXAMPLE_ADVERT_HEX, encryptedNote, errorText, isBlank, messageLine, messageSource, type DecodeResponse } from "../lib/decodeTool";
import type { Packet } from "../api/types";

// Paste a MeshCore packet as hex and see it decoded. The server decodes it in memory and answers; nothing is
// stored, no key is learned and nothing is sent anywhere. Channel messages are read only with the keys we hold.
export function DecodePage() {
  const { select } = useAppData();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DecodeResponse | null>(null);

  async function decode(hex: string) {
    if (isBlank(hex)) {
      setError("Paste a packet as hex first.");
      setResult(null);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setResult(await apiPost<DecodeResponse>("/api/decode/meshcore", { hex }));
    } catch (e) {
      setResult(null);
      setError(e instanceof ApiError ? errorText(e.status, e.message) : errorText(undefined, ""));
    } finally {
      setBusy(false);
    }
  }

  const line = result ? messageLine(result.message) : null;
  const note = result ? encryptedNote(result) : null;

  return (
    <div className="node-list-page">
      <h2 className="audit-page-title">Packets</h2>
      <PacketsViewSwitch active="decode" onSelect={(v) => navigate(packetsViewHash(v))} />

      <p className="node-detail-note">
        Paste a MeshCore packet as hex to see what is in it. Spaces, new lines, colons and a leading 0x are fine. Nothing is saved or sent anywhere.
      </p>

      <textarea
        className="decode-input"
        aria-label="Packet hex"
        rows={5}
        spellCheck={false}
        placeholder="12 00 0E CF 16 D5 ..."
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="decode-actions">
        <button className="settings-save-btn" disabled={busy} onClick={() => decode(text)}>
          {busy ? "Decoding…" : "Decode"}
        </button>
        <button
          className="sign-out-btn"
          onClick={() => {
            setText(EXAMPLE_ADVERT_HEX);
            decode(EXAMPLE_ADVERT_HEX);
          }}
        >
          Example advert
        </button>
        {text && (
          <button
            className="sign-out-btn"
            onClick={() => {
              setText("");
              setResult(null);
              setError(null);
            }}
          >
            Clear
          </button>
        )}
      </div>

      {error && (
        <div className="decode-error" role="alert">
          {error}
        </div>
      )}

      {result && (
        <div className="decode-result">
          {result.stored && (
            <div className="decode-stored">
              We have this packet:{" "}
              <button className="decode-link" onClick={() => select({ kind: "packet", packet: result.stored as unknown as Packet })}>
                open it
              </button>
            </div>
          )}

          <div className="k decode-heading">Decoded</div>
          <dl className="decode-summary">
            {result.summary.map((r) => (
              <div key={r.label} className="decode-summary-row">
                <dt>{r.label}</dt>
                <dd>{r.value}</dd>
              </div>
            ))}
            <div className="decode-summary-row">
              <dt>Packet hash</dt>
              <dd>{result.wire_hash}</dd>
            </div>
          </dl>

          {result.message && line && (
            <div className="decode-message">
              <div className="decode-message-text">{line}</div>
              <div className="node-detail-note">{messageSource(result.message)}</div>
            </div>
          )}
          {note && <div className="node-detail-note">{note}</div>}

          <div className="k decode-heading">Bytes</div>
          <PacketBytesView
            key={result.hex}
            data={{ packet_hash: "", available: true, source: null, observer: null, path_hops: null, length: result.length, hex: result.hex, fields: result.fields, note: null }}
            routeHops={0}
            caption={`${result.length} ${result.length === 1 ? "byte" : "bytes"}, as pasted`}
          />
        </div>
      )}
    </div>
  );
}
